import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { handleError } from '../../../../../shared/ErrorCatching';
import * as Actions from '../Actions';
import { IEmail, ISms, Recipients } from '../Types';

type UseCommunicationModalsProps = {
    orderId: number;
    sms: ISms[];
};

export function useCommunicationModals(props: UseCommunicationModalsProps) {
    const { orderId, sms } = props;
    const dispatch = useDispatch<any>();

    const [showEmailListModal, setShowEmailListModal] = useState(false);
    const [showSmsListModal, setShowSmsListModal] = useState(false);
    const [showCreateMailModal, setShowCreateMailModal] = useState(false);
    const [showCreateSmsModal, setShowCreateSmsModal] = useState(false);
    const [showViewEmailDetailsModal, setShowViewEmailDetailsModal] = useState(false);
    const [showViewSmsDetailsModal, setShowViewSmsDetailsModal] = useState(false);
    const [viewCommunicationDetailsRecipients, setViewCommunicationDetailsRecipients] = useState<
        string[]
    >([]);
    const [viewCommunicationDetailsBody, setViewCommunicationDetailsBody] = useState('');
    const [viewCommunicationDetailsBodyIsHtml, setViewCommunicationDetailsBodyIsHtml] =
        useState(false);
    const [viewCommunicationDetailsHasFile, setViewCommunicationDetailsHasFile] = useState(false);
    const [viewCommunicationDetailsAttachments, setViewCommunicationDetailsAttachments] = useState<
        any[]
    >([]);

    const openShowEmailListModal = (): void => {
        setShowEmailListModal(true);
    };

    const closeShowEmailListModal = (): void => {
        setShowEmailListModal(false);
    };

    const openShowSmsListModal = (): void => {
        dispatch(Actions.viewSms(orderId))
            .then(() => {
                setShowSmsListModal(true);
            })
            .catch((error: any) => handleError(error));
    };

    const closeShowSmsListModal = (): void => {
        setShowSmsListModal(false);
    };

    const openCreateMailModal = (recipient: Recipients): void => {
        switch (recipient) {
            case Recipients.Customer:
            case Recipients.Photographer:
            case Recipients.Owner:
                dispatch(Actions.newEmail(orderId, recipient))
                    .then(() => {
                        setShowCreateMailModal(true);
                    })
                    .catch((error: any) => handleError(error));
                return;
        }
    };

    const closeCreateMailModal = (): void => {
        setShowCreateMailModal(false);
    };

    const openCreateSmsModal = (recipient: Recipients): void => {
        switch (recipient) {
            case Recipients.Customer:
            case Recipients.Photographer:
            case Recipients.Owner:
                dispatch(Actions.newSms(orderId, recipient))
                    .then(() => {
                        setShowCreateSmsModal(true);
                    })
                    .catch((error: any) => handleError(error));
                return;

            case Recipients.BilderAccount:
            default:
                return;
        }
    };

    const closeCreateSmsModal = (): void => {
        setShowCreateSmsModal(false);
    };

    const openViewEmailDetailsModal = (email: IEmail): void => {
        const recipients = email.toRecipients
            .concat(email.ccRecipients.map((x) => x + ' (CC)'))
            .concat(email.bccRecipients.map((x) => x + ' (Bcc)'));

        setShowViewEmailDetailsModal(true);
        setViewCommunicationDetailsRecipients(recipients);
        setViewCommunicationDetailsBody(email.body);
        setViewCommunicationDetailsBodyIsHtml(email.isHtml);
        setViewCommunicationDetailsHasFile(email.hasFile);
        setViewCommunicationDetailsAttachments(email.emailAttachmentViewModel);
    };

    const openViewSmsDetailsModal = (smsId: number): void => {
        const smsToView = sms.filter((x: ISms) => x.id === smsId)[0];

        setShowViewSmsDetailsModal(true);
        setViewCommunicationDetailsRecipients(smsToView.recipients);
        setViewCommunicationDetailsBody(smsToView.body);
    };

    const closeViewCommunicationDetailsModal = (): void => {
        setShowViewEmailDetailsModal(false);
        setShowViewSmsDetailsModal(false);
        setViewCommunicationDetailsRecipients([]);
        setViewCommunicationDetailsBody('');
        setViewCommunicationDetailsHasFile(false);
        setViewCommunicationDetailsAttachments([]);
    };

    return {
        showEmailListModal,
        showSmsListModal,
        showCreateMailModal,
        showCreateSmsModal,
        showViewEmailDetailsModal,
        showViewSmsDetailsModal,
        viewCommunicationDetailsRecipients,
        viewCommunicationDetailsBody,
        viewCommunicationDetailsBodyIsHtml,
        viewCommunicationDetailsHasFile,
        viewCommunicationDetailsAttachments,
        openShowEmailListModal,
        closeShowEmailListModal,
        openShowSmsListModal,
        closeShowSmsListModal,
        openCreateMailModal,
        closeCreateMailModal,
        openCreateSmsModal,
        closeCreateSmsModal,
        openViewEmailDetailsModal,
        openViewSmsDetailsModal,
        closeViewCommunicationDetailsModal
    };
}
